import {Logger} from '../utils/Logger';
import {Validator} from '../utils/Validator';
import {Scheduler} from '../utils/Scheduler';
import {HttpClient} from '@angular/common/http';
import {Toaster, Type} from '../toaster/Toaster';
import {UserService} from '../service/user.service';
import {ActivatedRoute, Router} from '@angular/router';
import {ApiServiceService} from '../api-service.service';
import {AnalyticsTracker} from '../utils/AnalyticsTracker';
import {Keys, StorageService} from '../service/storage.service';
import {VerificationState} from '../model/verification-state.model';
import {ApplicationFlowNavigator} from '../application-flow/application-flow.navigator';
import {AbstractApplicationFlowComponent} from '../application-flow/abstract-application-flow.component';

export abstract class BasePhoneVerificationComponent extends AbstractApplicationFlowComponent {
    checkError = false;
    phoneNumber: string;
    otpButton = 'SEND OTP';
    mobileErrorMsg: string;
    mobileOtpError = false;
    fieldDisabledTag = false;
    sendOtpButtonEvent = false;
    public mobileError = false;
    public showOtp = false;
    public isReadonly: boolean;
    public phoneVerificationToken: string;
    public analyticsTracker: AnalyticsTracker;
    protected readonly http: HttpClient;
    protected readonly validator: Validator;
    protected readonly scheduler: Scheduler;
    protected readonly ref: ApiServiceService;
    protected readonly userService: UserService;
    protected readonly storageService: StorageService;
    protected readonly router: Router;
    protected readonly activatedRoute: ActivatedRoute;

    constructor(http: HttpClient, validator: Validator, ref: ApiServiceService, storageService: StorageService,
                scheduler: Scheduler, userService: UserService, router: Router, activatedRoute: ActivatedRoute,
                analyticsTracker: AnalyticsTracker, applicationFlowNavigator: ApplicationFlowNavigator) {
        super(applicationFlowNavigator);
        this.ref = ref;
        this.http = http;
        this.router = router;
        this.validator = validator;
        this.scheduler = scheduler;
        this.userService = userService;
        this.storageService = storageService;
        this.activatedRoute = activatedRoute;
        this.analyticsTracker = analyticsTracker;
    }

    async sendOtp() {
        this.mobileOtpError = false;
        this.mobileError = false;
        this.checkError = false;
        if (this.validator.isMobilePhone(this.phoneNumber)) {
            this.isReadonly = true;
            this.fieldDisabledTag = true;
            try {
                const phoneVerificationRequestToken = this.storageService.getObjectFromSession<string>(Keys.PHONE_VERIFICATION_REQUEST_TOKEN);
                const phoneVerificationToken = await this.userService.requestForPhoneVerification(this.phoneNumber, phoneVerificationRequestToken);
                this.onOtpSentSuccessfully(phoneVerificationToken);
            } catch (error) {
                this.showOtp = false;
                this.isReadonly = false;
                this.mobileError = true;
                this.fieldDisabledTag = false;
                this.mobileErrorMsg = 'Something went wrong. Please try again later.';
            }
        } else {
            this.mobileError = true;
            this.mobileErrorMsg = 'Please provide a valid mobile number';
        }
    }

    abstract onUserCreatedSuccessfully(): Promise<void>;

    abstract onPhoneVerificationFailed(error: any): Promise<void>;

    abstract onUserCreationFailed(): Promise<void>;

    async navigateToNextStep(): Promise<void> {
        await this.router.navigate(['../email-verification'], {relativeTo: this.activatedRoute});
    }

    protected enableResendOtp = () => {
        if (!this.isPhoneNumberVerified()) {
            this.fieldDisabledTag = false;
            this.isReadonly = false;
        }
    };

    private isPhoneNumberVerified(): boolean {
        return this.storageService.getItem(Keys.IS_OTP_VERIFIED) === 'true';
    }

    protected onOtpSentSuccessfully(phoneVerificationToken: string): void {
        this.showOtp = true;
        this.otpButton = 'RESEND OTP';
        this.sendOtpButtonEvent = true;
        this.phoneVerificationToken = phoneVerificationToken;
        this.scheduler.scheduleCallback(30 * 1000, this.enableResendOtp);
    }

    protected async verifyOtpAndCreateUser(otp: string): Promise<void> {
        let userCreationToken;
        try {
            userCreationToken = await this.userService.verifyPhoneForRegistration(otp, this.phoneVerificationToken);
        } catch (error) {
            this.onPhoneVerificationFailed(error);
            this.analyticsTracker.trackError('Wrong OTP entered');
            return;
        }
        try {
            const referrerCode = this.storageService.getObjectFromSession<VerificationState>(Keys.VERIFICATION_STATE) === VerificationState.VERIFIED ?
                this.storageService.getObjectFromSession<string>(Keys.REFERRER_CODE) : undefined;
            const onboardedBy = this.storageService.getObjectFromSession<VerificationState>(Keys.VERIFICATION_STATE) === VerificationState.PARTNER_VERIFIED ?
                this.storageService.getObjectFromSession<string>(Keys.REFERRER_CODE) : undefined;
            const {userId, authToken} = await this.userService.createUser(userCreationToken, referrerCode, onboardedBy);

            this.storageService.saveItem(Keys.IS_OTP_VERIFIED, true.toString());
            this.storageService.saveItem(Keys.PROFILE_ID, userId);
            this.storageService.saveItem(Keys.TOKEN, authToken);
            this.analyticsTracker.setUserId();
            this.analyticsTracker.sendAuthenticationEvent();
            this.clearTemporaryTokensFromStorage();

            await this.onUserCreatedSuccessfully();
        } catch (error) {
            Logger.log(`Something went wrong while creating user or the sequential steps. ${error.stack}`);
            Toaster.toast(`Something went wrong. Please try again later.`, Type.Error);
            this.onUserCreationFailed();
        }
    }

    private clearTemporaryTokensFromStorage() {
        this.storageService.removeObjectFromSession(Keys.PHONE_VERIFICATION_REQUEST_TOKEN);
        this.storageService.removeObjectFromSession(Keys.PAN);
    }
}
